import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Mic, Square, Sparkles, Cpu, Database, Server, TrendingUp, Loader2, Send } from 'lucide-react';
import { pickMime } from '../../hooks/useVoiceCapture';

/**
 * DestineIAAvatar — animated avatar using the official Destine popcorn logo.
 * Admins can talk (mic) or type to Destine IA; below: live learning metrics.
 */
export default function DestineIAAvatar() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listening, setListening] = useState(false);
  const [reply, setReply] = useState('');
  const [question, setQuestion] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/api/admin/destine-ia-progress');
        setProgress(r.data || null);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const ask = async (q) => {
    if (!q) { setReply("Je n'ai pas compris, réessayez."); return; }
    setQuestion(q);
    setBusy(true);
    try {
      const chat = await axios.post('/api/film-ia/chat', { message: q }, { timeout: 60000 });
      const txt = (chat.data?.response || chat.data?.reply || chat.data?.message || '').trim();
      if (!txt) throw new Error('empty');
      setReply(txt);
      try {
        const tts = await axios.post('/api/film-ia/text-to-speech', { text: txt.slice(0, 1200) }, { timeout: 60000 });
        if (tts.data?.audio) new Audio(`data:audio/mp3;base64,${tts.data.audio}`).play().catch(() => {});
      } catch { /* silent tts fallback */ }
    } catch (e) {
      setReply(e.response?.data?.detail || "Destine IA est momentanément indisponible, réessayez.");
    } finally { setBusy(false); }
  };

  const startListen = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { mime, ext } = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, `q.${ext}`);
          const stt = await axios.post('/api/film-ia/speech-to-text', fd, { timeout: 60000 });
          await ask((stt.data?.text || '').trim());
        } catch (e) {
          setReply(e.response?.data?.detail || 'Transcription impossible, réessayez.');
          setBusy(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setListening(true);
    } catch { setReply('Micro non autorisé.'); }
  };

  const stopListen = () => {
    if (mediaRef.current && listening) {
      mediaRef.current.stop();
      setListening(false);
    }
  };

  return (
    <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-fuchsia-950/70 via-purple-950/60 to-black border border-fuchsia-500/30" data-testid="destine-ia-avatar">
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32 mb-3" data-testid="ia-avatar-orb">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-purple-700 animate-pulse-slow shadow-2xl shadow-fuchsia-500/50" />
          <div className="absolute inset-1 rounded-full border border-white/30 animate-spin-slow border-dashed" />
          <div className="absolute inset-0 flex items-center justify-center">
            <img src="/app-icon.png" alt="Destine IA" className={`w-24 h-24 rounded-full object-cover border-2 border-white/40 ${busy ? 'animate-pulse' : ''}`} data-testid="ia-avatar-logo" />
          </div>
          {(listening || busy) && (
            <div className="absolute -inset-2 rounded-full border-4 border-fuchsia-400/60 animate-ping" />
          )}
        </div>

        <h3 className="text-lg font-bold bg-gradient-to-r from-fuchsia-300 to-pink-300 bg-clip-text text-transparent">Destine IA</h3>
        <p className="text-[11px] text-fuchsia-200/60 text-center max-w-xs mt-0.5 mb-3">
          Parlez ou écrivez à Destine IA et suivez son évolution en temps réel.
        </p>

        <div className="flex items-center gap-2 mb-3 w-full max-w-sm">
          <button
            onClick={listening ? stopListen : startListen}
            disabled={busy}
            className={`px-4 py-2.5 rounded-full font-semibold text-xs flex items-center gap-2 shrink-0 ${
              listening ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-r from-fuchsia-500 to-pink-500'
            } disabled:opacity-50`}
            data-testid="ia-avatar-mic"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : listening ? <Square className="w-4 h-4" fill="white" /> : <Mic className="w-4 h-4" />}
            {busy ? 'Réflexion…' : listening ? 'Arrêter' : 'Parler'}
          </button>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim() && !busy) { ask(typed.trim()); setTyped(''); } }}
            placeholder="Écrire à l'IA…"
            className="flex-1 min-w-0 px-3 py-2 rounded-full bg-black/40 border border-fuchsia-500/30 text-xs focus:outline-none focus:border-fuchsia-400"
            data-testid="ia-avatar-input"
          />
          <button onClick={() => { if (typed.trim() && !busy) { ask(typed.trim()); setTyped(''); } }} disabled={busy || !typed.trim()} className="p-2 rounded-full bg-fuchsia-500/30 disabled:opacity-40" data-testid="ia-avatar-send"><Send className="w-4 h-4" /></button>
        </div>

        {question && <p className="w-full text-[11px] text-fuchsia-200/70 mb-1 truncate" data-testid="ia-avatar-question">Vous : {question}</p>}
        {reply && (
          <div className="w-full p-3 rounded-xl bg-black/40 border border-fuchsia-500/20 text-xs text-fuchsia-100 mb-3 max-h-40 overflow-y-auto whitespace-pre-wrap" data-testid="ia-avatar-reply">
            {reply}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-300" /></div>
      ) : progress ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-fuchsia-300" />
            <span className="text-xs font-semibold text-fuchsia-200">Progression d&apos;apprentissage</span>
          </div>
          <div>
            <div className="flex justify-between text-[10px] mb-1">
              <span className="text-fuchsia-200/70">Niveau d&apos;intelligence</span>
              <span className="font-bold text-fuchsia-300">{progress.intelligence_score}/100</span>
            </div>
            <div className="w-full h-2 rounded-full bg-black/40 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fuchsia-500 via-pink-500 to-purple-500 transition-all duration-1000" style={{ width: `${progress.intelligence_score}%` }} />
            </div>
            <p className="text-[10px] text-fuchsia-200/50 mt-1">{progress.competitor_position}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <StatTile icon={<Cpu className="w-3.5 h-3.5" />} label="Modèles IA" value={progress.models_count} />
            <StatTile icon={<Database className="w-3.5 h-3.5" />} label="Bases de données" value={progress.databases_count} />
            <StatTile icon={<Server className="w-3.5 h-3.5" />} label="Serveurs actifs" value={progress.active_servers} />
            <StatTile icon={<Sparkles className="w-3.5 h-3.5" />} label="Points de savoir" value={progress.knowledge_points?.toLocaleString?.() || progress.knowledge_points} />
          </div>
          {progress.capabilities?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-fuchsia-200/70 mb-1">Capacités actives</p>
              <div className="flex flex-wrap gap-1">
                {progress.capabilities.map((c, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/30 text-[10px] text-fuchsia-200">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <style>{`
        @keyframes pulse-slow { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: 0.85; } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 18s linear infinite; }
      `}</style>
    </div>
  );
}

const StatTile = ({ icon, label, value }) => (
  <div className="p-2 rounded-xl bg-black/40 border border-fuchsia-500/20">
    <div className="flex items-center gap-1 text-[10px] text-fuchsia-200/70 mb-0.5">{icon}{label}</div>
    <div className="text-sm font-bold text-white">{value ?? '—'}</div>
  </div>
);
